import { ReactNode } from 'react';
import {
	Accordion,
	Anchor,
	BackgroundImage,
	Badge,
	Burger,
	Button,
	Center,
	Checkbox,
	Collapse,
	Divider,
	Drawer,
	Group,
	LoadingOverlay,
	MantineProvider as Mantine,
	MantineTheme,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Overlay,
	Pagination,
	PasswordInput,
	Notification,
	Popover,
	Progress,
	Radio,
	RadioGroup,
	rem,
	ScrollArea,
	ScrollAreaAutosize,
	Select,
	Slider,
	Stack,
	Textarea,
	TextInput,
	Timeline,
	Title,
	Tooltip,
	Loader as MantineLoader,
	Image,
	Flex,
	Input,
	Card,
	Code,
	ThemeIcon,
	Table,
	Skeleton,
	Stepper,
	ColorPicker,
	NumberInput,
	Avatar,
} from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Loader } from '@/components/partials/loading/partials/Loader';
import { Dropzone } from '@mantine/dropzone';
import { DatePicker } from '@mantine/dates';
import IconInfoCircle from '@/assets/icons/outline/info-circle.svg?react';
import IconChevronDown from '@/assets/icons/outline/chevron-down.svg?react';
import IconExclamationCircle from '@/assets/icons/outline/exclamation-circle.svg?react';
import IconEye from '@/assets/icons/outline/eye.svg?react';
import IconCheck from '@/assets/icons/outline/check.svg?react';
import APP_CONFIG from '@/configs/appConfig';
import classNames from 'classnames';
import styles from '../sass/mantine.module.scss';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';

const components: MantineThemeOverride['components'] = {
	Flex: Flex.extend({
		defaultProps: {
			align: 'center',
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {
			radius: 0,
			withControls: true,
			gap: 'sm',
		},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Burger: Burger.extend({
		defaultProps: {
			size: 30,
		},
	}),
	Code: Code.extend({
		defaultProps: {},
	}),
	ThemeIcon: ThemeIcon.extend({
		defaultProps: {
			radius: 0,
			color: 'gray',
		},
	}),
	Badge: Badge.extend({
		defaultProps: {
			color: 'dark',
			radius: 0,
		},
		classNames: {},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			withCloseButton: true,
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
			overlayProps: {
				opacity: 0.4,
				color: 'dark',
			},
			transitionProps: {
				transition: 'slide-down',
			},
			padding: 'xl',
			closeButtonProps: { size: 24 },
			size: `min(95dvw, ${rem(528)})`,
		},
		classNames: {
			header: styles.modalHeader,
			body: styles.modalBody,
			close: styles.modalClose,
			title: styles.modalTitle,
			inner: styles.modalInner,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			variant: 'filled',
			size: 'lg',
			radius: 0,
		},
	}),
	Menu: Menu.extend({
		defaultProps: {
			radius: 0,
			shadow: 'sm',
			transitionProps: {
				transition: 'fade',
			},
		},
		classNames: {
			item: styles.menuItem,
			itemLabel: styles.menuItemLabel,
			dropdown: styles.menuDropdown,
		},
	}),
	Collapse: Collapse.extend({
		defaultProps: {
			animateOpacity: true,
		},
	}),
	Stepper: Stepper.extend({
		defaultProps: {
			radius: 0,
			iconSize: 24,
		},
		classNames: {
			step: styles.stepperStep,
		},
	}),
	Avatar: Avatar.extend({
		classNames: {
			root: styles.avatarRoot,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			withTableBorder: true,
			withRowBorders: true,
			withColumnBorders: true,
			horizontalSpacing: 'sm',
			bg: 'white',
		},
	}),
	Divider: Divider.extend({
		defaultProps: {
			size: 1,
		},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Card: Card.extend({
		defaultProps: {
			radius: 0,
			p: 'xl',
			bg: 'white',
			bd: '1px solid var(--mantine-color-gray-2)',
		},
	}),
	Select: Select.extend({
		defaultProps: {
			allowDeselect: false,
			withCheckIcon: false,
			rightSection: <IconChevronDown />,
			rightSectionWidth: 30,
			size: 'lg',
			radius: 0,
			comboboxProps: {
				radius: 0,
				transitionProps: {
					duration: 100,
					transition: 'scale-y',
					timingFunction: 'ease',
				},
			},
		},
		classNames: {
			input: styles.selectInput,
			section: styles.selectSection,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
			error: styles.textInputError,
			label: styles.textInputLabel,
		},
	}),
	Image: Image.extend({
		defaultProps: {
			fit: 'contain',
		},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {
			rightSection: <IconChevronDown />,
		},
		classNames: {
			input: styles.nativeSelectInput,
		},
	}),
	ScrollArea: ScrollArea.extend({
		defaultProps: {
			scrollbarSize: 7,
			type: 'auto',
			['data-scrollarea']: 'true',
		},
		classNames: {
			scrollbar: styles.scrollAreaScrollbar,
			thumb: styles.scrollAreaThumb,
		},
	}),
	ScrollAreaAutosize: ScrollAreaAutosize.extend({
		defaultProps: {
			scrollbarSize: 7,
			type: 'auto',
		},
		classNames: {
			scrollbar: styles.scrollAreaScrollbar,
			thumb: styles.scrollAreaThumb,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			position: 'top',
			transitionProps: {
				transition: 'pop',
				duration: 200,
			},
			multiline: true,
			withArrow: true,
			arrowSize: 8,
			events: { hover: true, focus: true, touch: true },
			color: 'black',
			radius: 0,
		},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({
		defaultProps: {
			size: 'xs',
		},
	}),
	Radio: Radio.extend({
		defaultProps: {
			size: 'xs',
		},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
			inner: styles.radioInner,
			radio: styles.radioRadio,
			icon: styles.radioIcon,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			underline: 'never',
			c: 'dark.9',
		},
	}),
	Stack: Stack.extend({
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		classNames: {},
		defaultProps: {
			radius: 0,
			size: 'md',
			color: 'dark',
		},
	}),
	Input: Input.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
		},
		classNames: {
			input: styles.textInputInput,
			error: styles.textInputError,
			label: styles.textInputLabel,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
		},
		classNames: {
			label: styles.textInputLabel,
		},
	}),
	Skeleton: Skeleton.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			radius: 0,
			autosize: true,
			minRows: 4,
			maxRows: 8,
			size: 'lg',
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
			visibilityToggleIcon: ({ reveal }) => (
				<Center
					className={classNames({
						[styles.passwordInputIconRevealed]: reveal,
					})}
				>
					<IconEye color="var(--mantine-color-dimmed)" />
				</Center>
			),
		},
		classNames: {
			input: styles.textInputInput,
			error: styles.textInputError,
			label: styles.textInputLabel,
			section: styles.passwordInputSection,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {
			loaders: { ...MantineLoader.defaultLoaders, spinner: Loader },
			type: 'spinner',
		},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {
			loaderProps: {
				type: 'spinner',
			},
			overlayProps: {
				opacity: 0.5,
			},
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Overlay: Overlay.extend({
		defaultProps: {
			zIndex: APP_CONFIG.Z_INDEX.OVERLAY,
		},
	}),
	Progress: Progress.extend({
		defaultProps: {
			size: 'md',
			color: 'green.6',
			radius: 'md',
		},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {
			labelTransitionProps: {
				transition: 'fade',
				duration: 250,
			},
			thumbSize: 22,
			size: 6,
			label: null,
		},
		classNames: {
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
		},
	}),
	Timeline: Timeline.extend({
		defaultProps: {
			bulletSize: 30,
			lineWidth: 3,
		},
		classNames: {
			itemTitle: styles.timelineItemTitle,
			itemBullet: styles.timelineItemBullet,
		},
	}),
	DatePicker: DatePicker.extend({
		classNames: {
			day: styles.datePickerDay,
		},
	}),
	Drawer: Drawer.extend({
		defaultProps: {
			position: 'right',
			zIndex: APP_CONFIG.Z_INDEX.DRAWER,
			size: 592,
			closeButtonProps: {
				iconSize: 20,
			},
		},
		classNames: {
			overlay: styles.drawerInner,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {
			lts: -0.25,
			style: {
				textWrap: 'balance',
			},
		},
	}),
	Notification: Notification.extend({
		defaultProps: { withCloseButton: false, py: 'md', px: 'sm', radius: 0 },
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			icon: styles.notificationIcon,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {
			bgr: 'no-repeat',
			bgsz: 'contain',
			h: '100%',
		},
	}),
	ColorPicker: ColorPicker.extend({
		defaultProps: {
			format: 'hex',
			swatches: [
				'#2e2e2e',
				'#868e96',
				'#fa5252',
				'#e64980',
				'#be4bdb',
				'#7950f2',
				'#4c6ef5',
				'#228be6',
				'#15aabf',
				'#12b886',
				'#40c057',
				'#82c91e',
				'#fab005',
				'#fd7e14',
			],
		},
		classNames: {
			saturationOverlay: styles.colorPickerSaturationOverlay,
			sliderOverlay: styles.colorPickerSliderOverlay,
			swatches: styles.colorPickerSwatches,
			preview: styles.colorPickerPreview,
		},
	}),
	Accordion: Accordion.extend({
		defaultProps: {
			chevron: <IconChevronDown />,
			chevronPosition: 'left',
			transitionDuration: 250,
		},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			shadow: 'md',

			withArrow: false,
		},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
	Dropzone: Dropzone.extend({
		defaultProps: {
			bg: 'gray.0',
			bd: 'dashed 1px var(--mantine-color-gray-4)',
			style: {
				cursor: 'pointer',
			},
		},
	}),
};

const fontSizes: MantineTheme['fontSizes'] = {
	xs: rem(11),
	sm: rem(12),
	md: rem(14),
	lg: rem(16),
	xl: rem(18),
};

const lineHeights: MantineTheme['lineHeights'] = {
	xs: rem(18),
	sm: rem(18),
	md: rem(20),
	lg: rem(24),
	xl: rem(30),
};

const headings: MantineThemeOverride['headings'] = {
	fontFamily: 'Inter, sans-serif',
	fontWeight: '600',
	sizes: {},
};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1];
	return acc;
}, {});

const spacing: MantineThemeOverride['spacing'] = {
	xs: rem(3),
	sm: rem(8),
	md: rem(16),
	lg: rem(24),
	xl: rem(32),
};

const other: MantineThemeOverride['other'] = {
	notificationProps: {
		info: {
			classNames: {
				root: styles.notificationInfo,
				icon: styles.notificationInfoIcon,
				description: styles.notificationInfoDescription,
			},
			icon: <IconInfoCircle color="var(--mantine-color-blue-6)" />,
		},
		danger: {
			classNames: {
				root: styles.notificationDanger,
				icon: styles.notificationDangerIcon,
				description: styles.notificationDangerDescription,
			},
			icon: <IconExclamationCircle color="var(--mantine-color-red-8)" />,
		},
		success: {
			classNames: {
				root: styles.notificationSuccess,
				icon: styles.notificationSuccessIcon,
				description: styles.notificationSuccessDescription,
			},
			icon: <IconCheck color="var(--mantine-color-teal-6)" />,
		},
	},
};

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	return (
		<Mantine
			theme={{
				fontFamily: 'Inter, sans-serif',
				black: '#121926',
				primaryColor: 'dark',
				primaryShade: 9,
				lineHeights,
				spacing,
				other,
				cursorType: 'pointer',
				components,
				headings,
				breakpoints,
				fontSizes,
			}}
		>
			<ModalsProvider>{children}</ModalsProvider>
		</Mantine>
	);
};
